import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import Renderable from '../../../Component/Renderable';
import Crab from '../../../Prototype/Level/Card/Crab';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import CrabCard from '../../../Prototype/Level/Card/CrabCard';
import Sprite from '../../../Prototype/Sprite';
import Point from '../../../Utility/Point';
import SpriteComponent from '../../../Component/Render/Sprite';
import { AwaitIterators } from '../AnimationHelper';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffect } from '../CardAnimationHelper';

interface ICardCrabsMeta {
    system: CardSystem;
    sourceCard: CrabCard;
    targetCards: BaseCard[];
}

export class CardCrabs extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardCrabsMeta
    ): IterableIterator<number> {
        animator.addChild(meta.sourceCard);

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        const crabJumps = [];

        for (let card of meta.targetCards) {
            animator.addChild(card);

            // Add as child to animator to bring in front of overlay.
            crabJumps.push(
                this.crabJump(meta.system, animator, card, meta.sourceCard)
            );
        }

        yield* AwaitIterators(crabJumps);

        meta.system.sortCardsInPlay(
            meta.system.state.getState().levelState.cardsState
        );
    }

    private *crabJump(
        system: CardSystem,
        animator: GameObject,
        targetCard: BaseCard,
        sourceCard: CrabCard
    ): IterableIterator<number> {
        const targetCardTransform = targetCard.getComponent(TransformComponent);

        const algaeContainer = targetCard.find('modifierContainer/algae');
        const algaeSprite = algaeContainer.find('algaeSprite');
        const algaeSpriteComponent = algaeSprite.getComponent(SpriteComponent);

        // Create another algae sprite for texture cross-fade.
        const prevAlgaeSprite = GameObject.Instantiate(Sprite, {
            name: 'prevAlgaeSprite',
            texture: algaeSpriteComponent.texture, // Set to current algae texture.
            anchor: new Point(0.5, 0.5),
        });
        const prevAlgaeSpriteComponent = prevAlgaeSprite.getComponent(
            SpriteComponent
        );
        algaeContainer.addChild(prevAlgaeSprite);

        const algaeTextures = ['algae_3', 'algae_2', 'algae_1'];

        const nextTextureIndex =
            algaeTextures.indexOf(algaeSpriteComponent.texture) + 1;

        // Set algae sprite to next texture or invisible.
        if (algaeTextures[nextTextureIndex]) {
            algaeSpriteComponent.texture = algaeTextures[nextTextureIndex];
        } else {
            algaeSpriteComponent.visible = false;
        }

        const prevAlgaeTweenAlpha = this.getTween(prevAlgaeSpriteComponent);

        const sourceCardTransform = sourceCard.getComponent(TransformComponent);
        const sourceCardRenderable = sourceCard.getComponent(Renderable);
        const alphaTween = this.getTween(sourceCardRenderable);
        const crab = GameObject.Instantiate(Crab, { clipName: 'CrabWiggle' });
        const crabTransform = crab.getComponent(TransformComponent);
        const crabRenderable = crab.getComponent(Renderable);
        const crabTweenAlpha = this.getTween(crabRenderable);
        const crabTweenX = this.getTween(crabTransform);
        const crabTweenY = this.getTween(crabTransform);

        animator.addChild(crab);

        crabTransform.position.set(
            sourceCardTransform.position.x,
            sourceCardTransform.position.y
        );

        const jumpDuration = 750;
        const snipDuration = 500;
        const fadeDuration = 150;

        const tweenHeight = Math.min(
            targetCardTransform.y - 100,
            sourceCardTransform.y - 200
        );

        alphaTween.to({ alpha: 0 }, fadeDuration).start(this.elapsedTime);

        crabTweenX
            .to({ x: targetCardTransform.x }, jumpDuration)
            .easing(Easing.Quadratic.Out)
            .start(this.elapsedTime);

        crabTweenY
            .to({ y: tweenHeight }, jumpDuration * 0.5)
            .easing(Easing.Quadratic.Out);

        yield* this.startTween(crabTweenY);

        crabTweenY
            .to({ y: targetCardTransform.y + 25 }, jumpDuration * 0.5)
            .easing(Easing.Quadratic.In);

        yield* this.startTween(crabTweenY);

        // Cross-fade algae textures.
        prevAlgaeSpriteComponent.alpha = 1;

        prevAlgaeTweenAlpha
            .to({ alpha: 0 }, fadeDuration)
            .start(this.elapsedTime);

        crabTweenY
            .to({ y: targetCardTransform.y - 25 }, snipDuration)
            .easing(Easing.Quadratic.InOut);

        yield* this.startTween(crabTweenY);

        crabTweenAlpha.to({ alpha: 0 }, 100);

        yield* this.startTween(crabTweenAlpha);

        crab.destroy();

        prevAlgaeSprite.destroy();

        if (!algaeSpriteComponent.visible) {
            showCardScoreEffect(system.scene, targetCard);
        }
    }
}
